import {
  Button,
  Col,
  DocumentListCard,
  GenericObjectCard,
  LinkButton,
  PublicButton,
  Row,
  SkeletonDots
} from '@yes.technology/react-toolkit'

import { useTranslation } from 'react-i18n-lite'
import { useImageDocument } from 'shared/hooks'
import {
  createQueryString,
  getPublicButtonRest,
  isPublicButton
} from 'shared/utils'
import useStyleMaps from 'site/Renderer/hooks/useStyleMap'
import { SafeHtmlContent } from 'site/shared/components/SafeHtmlContent'
import { useSiteState } from 'siteState/shared'
import { CardContainerProps, ObjectsTypes } from './CardContainer.types'
import { StyledContainer } from './styles'

type CardItemProps = {
  object: ObjectsTypes
  link: string
  linkParams?: []
  buttonProps?: CardContainerProps['props']['button']
  buttonLabel?: string
  imageProps?: React.HTMLProps<HTMLImageElement>
  imageHeight?: string
  imageFormat?: 'circle' | 'default'
}

interface RowRendererProps {
  objects: CardItemProps['object'][]
  justifyContent?: 'start' | 'end' | 'center'
  link: CardItemProps['link']
  linkParams?: CardItemProps['linkParams']
  buttonProps?: CardItemProps['buttonProps']
  buttonLabel?: string
  imageProps?: React.HTMLProps<HTMLImageElement>
  imageHeight?: string
  imageFormat?: 'circle' | 'default'
}

export function RowRenderer({
  objects,
  justifyContent = 'start',
  link,
  linkParams,
  buttonProps,
  buttonLabel,
  imageProps,
  imageHeight,
  imageFormat
}: RowRendererProps) {
  const chunkArray = (array: CardItemProps['object'][], size: number) => {
    const chunkedArr = []
    for (let i = 0; i < array?.length; i += size) {
      chunkedArr.push(array.slice(i, i + size))
    }
    return chunkedArr
  }

  const rows = chunkArray(objects, 4)

  return (
    <>
      {rows.map((row, rowIndex) => (
        <Row
          key={`row-${rowIndex}`}
          className={`justify-content-${justifyContent}`}
        >
          {row.map((object: CardItemProps['object']) => (
            <Col key={object.uuid} xl={3} className='mb-3 mb-sm-4'>
              <CardItem
                {...{ object }}
                link={link}
                linkParams={linkParams}
                buttonProps={buttonProps}
                buttonLabel={buttonLabel}
                {...{ imageProps, imageHeight, imageFormat }}
              />
            </Col>
          ))}
        </Row>
      ))}
    </>
  )
}

const CardContainer = ({ props }: CardContainerProps) => {
  const styleProps = useStyleMaps(props.styleMap)

  const SkeletonCard = () => (
    <DocumentListCard>
      <SkeletonDots size={4} />
      <Button variant='secondary' className='mt-3'>
        <SkeletonDots size={4} />
      </Button>
    </DocumentListCard>
  )

  const SkeletonCardList = () => (
    <div className={`row justify-content-center`}>
      <div className='col-xl-3 mb-3 mb-sm-4 mt-3 mt-sm-4'>
        <SkeletonCard />
      </div>
    </div>
  )

  const [allResultsFetched] = useSiteState('allResultsFetched')
  const isLoading = !allResultsFetched
  return (
    <>
      <StyledContainer style={styleProps}>
        {!isLoading && (
          <RowRenderer
            objects={props.objects}
            justifyContent={props.justifyContent}
            link={props.link}
            linkParams={props.linkParams}
            buttonProps={props.button}
            buttonLabel={props?.buttonLabel}
            imageProps={props?.imageProps}
            imageHeight={props?.imageHeight}
            imageFormat={props?.imageFormat}
          />
        )}
        {isLoading && <SkeletonCardList />}
      </StyledContainer>
    </>
  )
}

export const CardItem = ({
  object,
  link,
  linkParams,
  buttonProps,
  buttonLabel,
  imageProps,
  imageHeight,
  imageFormat
}: CardItemProps) => {
  const { imageUrl, isLoading: isLoadingDocument } = useImageDocument({
    document: object?.document || object?.documento || ''
  })

  const { t } = useTranslation()

  const buttonHref = `${link}?${createQueryString(object, linkParams || [])}`

  return (
    <GenericObjectCard
      {...object}
      des={<SafeHtmlContent html={object.des} />}
      image={(!isLoadingDocument && imageUrl) || ''}
      frameImage={true}
      fullHeight
      {...{ imageProps, imageHeight, imageFormat }}
      id={object.uuid}
    >
      {isPublicButton(buttonProps) ? (
        <PublicButton
          href={buttonHref}
          target={buttonProps?.target ?? '_self'}
          {...getPublicButtonRest(buttonProps)}
        >
          {buttonProps?.label ?? buttonLabel ?? t('card-container.learn-more')}
        </PublicButton>
      ) : (
        <LinkButton
          href={buttonHref}
          target={buttonProps?.target ?? '_self'}
          {...buttonProps}
        >
          {buttonLabel ?? t('card-container.learn-more')}
        </LinkButton>
      )}
    </GenericObjectCard>
  )
}

export default CardContainer
